import { useToast } from 'common/hooks/useToast';
import useTranslation from 'common/hooks/useTranslation';
import {
  useGetOfferChatBySlugQuery,
  useResetOfferAssistantMutation,
} from 'common/slices/offerApi.slice';
import { useEffect, useMemo, useState } from 'react';

export const useAssistantChat = ({ offerSlug }) => {
  const toast = useToast();
  const { t } = useTranslation('offers\\details');
  const [isPolling, setPolling] = useState(false);
  const [resetOfferAssistant] = useResetOfferAssistantMutation();
  const CHAT_STATUS = useMemo(() => {
    return {
      LOADING: 'loading',
      READY: 'ready',
      PENDING: 'pending',
      SYNCING: 'syncing',
      TIMEOUT: 'timeout',
      ERROR: 'error',
      UNKNOWN: 'unknown',
    };
  }, []);
  const [chatStatus, setChatStatus] = useState(CHAT_STATUS.LOADING);

  const {
    data: chatHistory,
    isLoading: isLoadingChat,
    isFetching: isFetchingChat,
    refetch: refetchChat,
  } = useGetOfferChatBySlugQuery(
    { offerSlug },
    {
      skip: !offerSlug,
    },
  );

  useEffect(() => {
    const status = chatHistory?.chat_assistant?.status;
    if (!status) return;

    const normalizedStatus =
      CHAT_STATUS[status.toUpperCase()] || CHAT_STATUS.UNKNOWN;

    if (isPolling && normalizedStatus === CHAT_STATUS.PENDING) {
      setChatStatus(CHAT_STATUS.SYNCING);
    } else {
      setChatStatus(normalizedStatus);
    }
  }, [chatHistory]);

  useEffect(() => {
    if (!isPolling) return;
    const poll = async () => {
      let retries = 0;
      while (retries < 10) {
        const { data } = await refetchChat();
        const status = data?.chat_assistant?.status;
        if (status === CHAT_STATUS.READY) {
          toast.newToast('positive', t('toasts.assistant-ready'));
          setPolling(false);
          return;
        }
        retries++;
        await new Promise((r) => setTimeout(r, 4000));
      }
      toast.newToast('warning', t('toasts.assistant-timeout'));
      setChatStatus(CHAT_STATUS.TIMEOUT);
      setPolling(false);
    };
    poll();
  }, [isPolling]);

  const handleResetAssistant = async () => {
    try {
      await resetOfferAssistant({ offerSlug }).unwrap();
      setPolling(true);
      toast.newToast('positive', t('toasts.success-reset-assistant'));
    } catch (err) {
      setPolling(false);
      setChatStatus(CHAT_STATUS.ERROR);
      if (err?.status === 403) {
        toast.newToast('negative', t('toasts.forbidden'));
      } else {
        toast.newToast('negative', t('toasts.something-wrong'));
      }
    }
  };

  const chatInitialMessages = chatHistory?.messages.map((message) => ({
    text: message.value,
    role: message.role === 'user' ? 'user' : 'ai',
  }));

  const isChatReady = chatStatus === CHAT_STATUS.READY;
  const isChatBusy = isLoadingChat || isFetchingChat || isPolling;
  const isChatSyncing = isChatBusy || chatStatus === CHAT_STATUS.SYNCING;

  return {
    chatStatus,
    isChatReady,
    isChatSyncing,
    isChatBusy,
    chatInitialMessages,
    isLoadingChat,
    isFetchingChat,
    refetchChat,
    handleResetAssistant,
  };
};
